/**
 * Phase 3.44 — Dienstleister-Dashboard (Stammdaten + Slim Marketing).
 *
 * Eingeloggte ``service_provider``-Accounts können hier ihren Verzeichnis-
 * Eintrag pflegen. Beinhaltet zwei Abschnitte:
 *   1. Stammdaten (Profilbild, Firmenname, Ansprechpartner, Adresse)
 *   2. Marketing — Zielgruppen, Sozialmedia, Werbemittel & Tracking, Verknüpfung Händlerhomepage
 */
import { useEffect, useState } from "react";
import api from "../lib/api";
import ClubMembersCard from "../components/service_provider/ClubMembersCard";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Wrench, Save, Loader2, ShieldCheck, ShieldAlert, Building2, Target, Share2, BarChart3, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";

const SOCIAL_KEYS = ["instagram", "facebook", "tiktok", "youtube", "linkedin"];

function Section({ id, icon: Icon, title, hint, open, onToggle, children }) {
    return (
        <div className="bg-white border border-gray-200 rounded-sm overflow-hidden">
            <button
                type="button"
                onClick={onToggle}
                className="w-full flex items-center gap-3 px-5 py-4 bg-[#FFFBEB]/40 border-b border-gray-200 text-left"
                data-testid={`sp-dash-toggle-${id}`}
            >
                <Icon className="w-5 h-5 text-[#FFCC00] shrink-0" />
                <div className="flex-1 min-w-0">
                    <h2 className="font-display text-lg font-bold">{title}</h2>
                    {hint && <p className="text-xs text-gray-600 mt-1">{hint}</p>}
                </div>
                <span className={`text-gray-400 text-lg transition-transform shrink-0 ${open ? "rotate-180" : ""}`}>⌄</span>
            </button>
            {open && <div className="p-5 space-y-4">{children}</div>}
        </div>
    );
}

export default function ServiceProviderDashboard() {
    const { currentUser, refresh } = useAuth();
    const [profile, setProfile] = useState(null);
    const [form, setForm] = useState(null);
    const [open, setOpen] = useState({ stamm: true, marketing: false });
    const [saving, setSaving] = useState(false);
    const [branchLabel, setBranchLabel] = useState("");

    const load = async () => {
        try {
            const r = await api.get("/service-providers/me");
            setProfile(r.data);
            setForm({
                name: r.data.name || "",
                contact_first_name: r.data.contact_first_name || "",
                contact_last_name: r.data.contact_last_name || "",
                contact_phone: r.data.contact_phone || "",
                website: r.data.website || "",
                street: r.data.street || "",
                postal_code: r.data.postal_code || "",
                city: r.data.city || "",
                country: r.data.country || "Deutschland",
                regions: (r.data.regions || []).join(", "),
                description: r.data.description || "",
                price_from_eur: r.data.price_from_eur ?? "",
                company_logo_url: r.data.company_logo_url || "",
                target_audiences: (r.data.target_audiences || []).join(", "),
                social_links: r.data.social_links || {},
                tracking_pixel: r.data.tracking_pixel || "",
                homepage_link: r.data.homepage_link || "",
            });
        } catch (e) {
            toast.error(e.response?.data?.detail || "Profil konnte nicht geladen werden");
        }
    };

    useEffect(() => { load(); }, []);
    useEffect(() => {
        api.get("/service-providers/branches").then((r) => {
            const m = (r.data.items || []).find((b) => b.key === profile?.branch);
            if (m) setBranchLabel(m.label);
        });
    }, [profile?.branch]);

    if (!form || !profile) {
        return <div className="max-w-4xl mx-auto p-12 flex items-center gap-3 text-gray-600"><Loader2 className="w-5 h-5 animate-spin" /> Lade Profil…</div>;
    }

    const upd = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                ...form,
                regions: (form.regions || "").split(",").map((s) => s.trim()).filter(Boolean),
                target_audiences: (form.target_audiences || "").split(",").map((s) => s.trim()).filter(Boolean),
                price_from_eur: form.price_from_eur === "" || form.price_from_eur == null ? null : parseFloat(form.price_from_eur),
            };
            await api.patch("/service-providers/me", payload);
            await refresh();
            toast.success("Profil gespeichert");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
        } finally {
            setSaving(false);
        }
    };

    const setSocial = (k, v) => upd("social_links", { ...(form.social_links || {}), [k]: v });

    return (
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-8 space-y-6" data-testid="sp-dashboard">
            <div>
                <div className="text-xs font-bold uppercase tracking-wider text-[#FFCC00] flex items-center gap-2 mb-1">
                    <Wrench className="w-3.5 h-3.5" /> Dienstleister-Bereich
                </div>
                <div className="flex items-start gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                        <h1 className="font-display text-3xl md:text-4xl font-black truncate" data-testid="sp-dash-headline">{profile.name}</h1>
                        <div className="flex items-center gap-2 flex-wrap mt-2">
                            {branchLabel && <Badge className="bg-gray-100 text-gray-700 hover:bg-gray-100" data-testid="sp-dash-branch">{branchLabel}</Badge>}
                            {profile.verified ? (
                                <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 gap-1" data-testid="sp-dash-verified">
                                    <ShieldCheck className="w-3 h-3" /> Veröffentlicht
                                </Badge>
                            ) : (
                                <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 gap-1" data-testid="sp-dash-pending">
                                    <ShieldAlert className="w-3 h-3" /> Wartet auf Admin-Freigabe
                                </Badge>
                            )}
                        </div>
                        <p className="text-xs text-gray-500 mt-2">E-Mail: {currentUser?.email}</p>
                    </div>
                </div>
            </div>

            {/* Feb-2026 — Clubs / Vereine: Mitglieder-Erfassung & Code-Versand */}
            {profile.branch === "clubs_vereine" && <ClubMembersCard />}

            {/* Stammdaten */}
            <Section id="stamm" icon={Building2} title="Stammdaten" hint="Firma, Logo, Ansprechpartner, Adresse" open={open.stamm} onToggle={() => setOpen((p) => ({ ...p, stamm: !p.stamm }))}>
                <div className="flex items-center gap-3 mb-2">
                    {form.company_logo_url ? (
                        <img src={form.company_logo_url} alt="Logo" className="w-16 h-16 object-cover bg-gray-100 rounded-sm" />
                    ) : (
                        <div className="w-16 h-16 bg-[#111827] text-[#FFCC00] flex items-center justify-center rounded-sm font-display font-black text-xl">{(form.name || "?").charAt(0)}</div>
                    )}
                    <div className="flex-1">
                        <Label className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Logo-URL</Label>
                        <Input value={form.company_logo_url} onChange={(e) => upd("company_logo_url", e.target.value)} placeholder="https://…/logo.png" className="rounded-sm mt-1 text-sm font-mono-num" data-testid="sp-dash-logo-url" />
                    </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="sm:col-span-2">
                        <Label className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Firmenname *</Label>
                        <Input value={form.name} onChange={(e) => upd("name", e.target.value)} className="rounded-sm mt-1" data-testid="sp-dash-name" />
                        <p className="text-[10px] text-gray-500 mt-1">Wird auf Inseraten, Rechnungen und im Audit-Export angezeigt.</p>
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Vorname (Ansprechpartner)</Label>
                        <Input value={form.contact_first_name} onChange={(e) => upd("contact_first_name", e.target.value)} className="rounded-sm mt-1" data-testid="sp-dash-firstname" />
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Nachname (Ansprechpartner)</Label>
                        <Input value={form.contact_last_name} onChange={(e) => upd("contact_last_name", e.target.value)} className="rounded-sm mt-1" data-testid="sp-dash-lastname" />
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Telefon</Label>
                        <Input value={form.contact_phone} onChange={(e) => upd("contact_phone", e.target.value)} className="rounded-sm mt-1 font-mono-num" data-testid="sp-dash-phone" />
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Website</Label>
                        <Input type="url" value={form.website} onChange={(e) => upd("website", e.target.value)} className="rounded-sm mt-1 font-mono-num text-sm" data-testid="sp-dash-website" />
                    </div>
                    <div className="sm:col-span-2">
                        <Label className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Straße & Hausnr.</Label>
                        <Input value={form.street} onChange={(e) => upd("street", e.target.value)} className="rounded-sm mt-1" data-testid="sp-dash-street" />
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase tracking-wider font-bold text-gray-500">PLZ</Label>
                        <Input value={form.postal_code} onChange={(e) => upd("postal_code", e.target.value)} className="rounded-sm mt-1 font-mono-num" data-testid="sp-dash-postal" />
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Ort</Label>
                        <Input value={form.city} onChange={(e) => upd("city", e.target.value)} className="rounded-sm mt-1" data-testid="sp-dash-city" />
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Land</Label>
                        <Input value={form.country} onChange={(e) => upd("country", e.target.value)} className="rounded-sm mt-1" data-testid="sp-dash-country" />
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Einzugsgebiet (PLZ-Präfixe, kommasepariert)</Label>
                        <Input value={form.regions} onChange={(e) => upd("regions", e.target.value)} placeholder="10, 11, 12" className="rounded-sm mt-1 font-mono-num text-sm" data-testid="sp-dash-regions" />
                    </div>
                    <div className="sm:col-span-2">
                        <Label className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Beschreibung</Label>
                        <Textarea value={form.description} onChange={(e) => upd("description", e.target.value)} rows={3} className="rounded-sm mt-1 text-sm" data-testid="sp-dash-description" />
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Preise ab (€)</Label>
                        <Input type="number" min="0" step="1" value={form.price_from_eur} onChange={(e) => upd("price_from_eur", e.target.value)} className="rounded-sm mt-1 font-mono-num w-40" data-testid="sp-dash-price" />
                    </div>
                </div>
            </Section>

            {/* Marketing (Slim) */}
            <Section id="marketing" icon={Target} title="Marketing Die Zweiradhändler" hint="Zielgruppen · Sozialmedia · Werbemittel & Tracking · Verknüpfung Händlerhomepage" open={open.marketing} onToggle={() => setOpen((p) => ({ ...p, marketing: !p.marketing }))}>
                <div>
                    <Label className="text-[10px] uppercase tracking-wider font-bold text-gray-500 flex items-center gap-1"><Target className="w-3 h-3" /> Zielgruppen (kommasepariert)</Label>
                    <Input value={form.target_audiences} onChange={(e) => upd("target_audiences", e.target.value)} placeholder="z.B. Händler, Werkstätten, Endkunden" className="rounded-sm mt-1 text-sm" data-testid="sp-dash-audiences" />
                </div>
                <div>
                    <Label className="text-[10px] uppercase tracking-wider font-bold text-gray-500 flex items-center gap-1"><Share2 className="w-3 h-3" /> Sozialmedia-Links</Label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1">
                        {SOCIAL_KEYS.map((k) => (
                            <div key={k}>
                                <span className="text-[10px] text-gray-500 capitalize">{k}</span>
                                <Input
                                    type="url"
                                    value={(form.social_links || {})[k] || ""}
                                    onChange={(e) => setSocial(k, e.target.value)}
                                    placeholder={`https://${k}.com/...`}
                                    className="rounded-sm h-8 text-xs font-mono-num"
                                    data-testid={`sp-dash-social-${k}`}
                                />
                            </div>
                        ))}
                    </div>
                </div>
                <div>
                    <Label className="text-[10px] uppercase tracking-wider font-bold text-gray-500 flex items-center gap-1"><BarChart3 className="w-3 h-3" /> Werbemittel & Tracking (Pixel-ID / Snippet)</Label>
                    <Input value={form.tracking_pixel} onChange={(e) => upd("tracking_pixel", e.target.value)} placeholder="z.B. GA4-Mess-ID oder Meta-Pixel-ID" className="rounded-sm mt-1 font-mono-num text-sm" data-testid="sp-dash-tracking" />
                </div>
                <div>
                    <Label className="text-[10px] uppercase tracking-wider font-bold text-gray-500 flex items-center gap-1"><LinkIcon className="w-3 h-3" /> Verknüpfung Händler-Homepage</Label>
                    <Input type="url" value={form.homepage_link} onChange={(e) => upd("homepage_link", e.target.value)} placeholder="https://meine-firma.de" className="rounded-sm mt-1 font-mono-num text-sm" data-testid="sp-dash-homepage" />
                    <p className="text-[10px] text-gray-500 mt-1">Wird auf Ihrer Verzeichnis-Karte verlinkt.</p>
                </div>
            </Section>

            {/* Speichern */}
            <div className="flex justify-end sticky bottom-4">
                <Button
                    onClick={save}
                    disabled={saving}
                    className="bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-bold rounded-sm uppercase tracking-wider shadow-lg"
                    data-testid="sp-dash-save"
                >
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Profil speichern
                </Button>
            </div>
        </div>
    );
}
